import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Keyboard, Animated } from 'react-native';
import { BarCodeScanner } from 'expo-barcode-scanner';
import QrScannerProps from 'types/components/QrScannerProps';
import { useIsFocused } from '@react-navigation/native';
import { styles } from 'styles/components/qrScanner';
import { useTranslation } from 'react-i18next';

const QRScanner = ({ onRead, isOrientationPortrait, scanned, setShowCamera, isBarCode }: QrScannerProps) => {
  const { t } = useTranslation();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [cameraType, setCameraType] = useState(BarCodeScanner.Constants.Type.back);
  const [showReminder, setShowReminder] = useState(false);

  const isFocused = useIsFocused();
  const inputRef = useRef<TextInput>(null);
  const animation = useRef(new Animated.Value(0)).current;

  const handleBarCodeScanned = ({ data }: { data: string }) => {
    if (scanned) return;
    const parsed = parseInt(data);
    onRead(parsed);
  };

  useEffect(() => {
    if (scanned) {
      setShowReminder(false);
      return;
    }
    const interval = setInterval(() => {
      setShowReminder(true);
      setTimeout(() => setShowReminder(false), 2500);
    }, 7000);

    return () => clearInterval(interval);
  }, [scanned]);

  useEffect(() => {
    (async () => {
      const { status } = await BarCodeScanner.requestPermissionsAsync();
      setHasPermission(status === 'granted');

      if (isOrientationPortrait) {
        setCameraType(BarCodeScanner.Constants.Type.back);
      } else {
        setCameraType(BarCodeScanner.Constants.Type.front);
      }

      setTimeout(() => {
        inputRef.current?.focus();
      }, 500);
    })();

    const hideSub = Keyboard.addListener('keyboardDidHide', () => {
      inputRef.current?.focus();
    });

    return () => hideSub.remove();
  }, [isOrientationPortrait]);

  useEffect(() => {
    if (isBarCode) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(animation, {
            toValue: 250 - 3,
            duration: 1500,
            useNativeDriver: false,
          }),
          Animated.timing(animation, {
            toValue: 0,
            duration: 1500,
            useNativeDriver: false,
          }),
        ]),
      ).start();
    } else {
      animation.stopAnimation();
      animation.setValue(0);
    }
  }, [isBarCode, animation]);

  if (hasPermission === null) {
    return (
      <View style={styles.permissionContainer}>
        <Text style={styles.permissionText}>{t('camera-requesting-permission')}</Text>
        <TouchableOpacity style={styles.closeButton} onPress={() => setShowCamera(false)}>
          <Text style={styles.closeText}>{t('close')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (hasPermission === false) {
    return (
      <View style={styles.permissionContainer}>
        <Text style={styles.permissionText}>{t('camera-no-permission')}</Text>
        <Text style={styles.permissionHint}>{t('camera-no-permission-hint')}</Text>
        <TouchableOpacity style={styles.closeButton} onPress={() => setShowCamera(false)}>
          <Text style={styles.closeText}>{t('close')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.page2}>
      {isFocused && (
        <BarCodeScanner
          onBarCodeScanned={handleBarCodeScanned}
          style={StyleSheet.absoluteFillObject}
          type={cameraType}
        />
      )}

      {isBarCode && (
        <View style={styles.scannerContainer}>
          <Text style={styles.instructionText}>{t('scan-barcode-instruction')}</Text>
          <View style={styles.scannerFrame}>
            <Animated.View
              style={[
                styles.scanLine,
                {
                  transform: [{ translateY: animation }],
                  height: 3,
                  backgroundColor: '#24E016',
                },
              ]}
            />
          </View>
        </View>
      )}

      {showReminder && (
        <View style={isOrientationPortrait ? styles.reminderContainer : styles.reminderContainerLandscape}>
          <Text style={styles.reminderText}>{t('scanner-reminder')}</Text>
        </View>
      )}

      <View style={isOrientationPortrait ? styles.footer : styles.footerLandscape}>
        <TouchableOpacity style={styles.closeButton} onPress={() => setShowCamera(false)}>
          <Text style={styles.closeText}>{t('close')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default QRScanner;
